// TON adapter — ed25519 derivation and the wallet contract address.
//
// TON differs from every other chain here in one way that shapes the whole
// file: an address is not a hash of a public key, it is the hash of a CONTRACT.
// A TON wallet is a smart contract, and its address is the hash of its initial
// state — the wallet code plus the data cell holding the public key. So the
// address cannot be computed from the key alone; it depends on which wallet
// version the key is wrapped in, and two versions of the same key are two
// different addresses.
//
// That is why the contract's code and data layout live in ./wallet.ts as data
// rather than being hashed together ad hoc here.
// The wallet code is a compiled contract, the data layout is version specific,
// and a cell hash is a recursive structure with depths and refs — all of it
// money critical. The address computation here was verified against mainnet:
// a deployed v4r2 wallet and a deployed v5r1 wallet were read off the chain,
// their public keys fetched with get_public_key, and the addresses recomputed
// from those keys matched both accounts exactly.
//
// W5 (V5R1) is what we deploy: it is the current standard, it costs less per
// transfer than v4r2, and it is what new wallets create today. Receiving does
// not depend on the version — anyone can send to any address — so the choice
// only affects our own sends.
//
// ── On seed compatibility ───────────────────────────────────────────────────
// Native TON wallets (Tonkeeper, MyTonWallet) do NOT use BIP-39. They have
// their own mnemonic scheme: 24 words validated by a PBKDF2 round over the
// string "TON default seed", which produces the ed25519 key directly with no
// BIP-32/SLIP-10 path at all. Our seed is BIP-39, shared with every other
// chain in the wallet, so the keys cannot coincide.
//
// The consequence is mutual and it is not a bug to be fixed later: our TON
// address cannot be imported into Tonkeeper, and a Tonkeeper seed cannot be
// imported here. Deriving TON from the BIP-39 seed is the deliberate trade —
// one seed phrase for the whole wallet, at the cost of TON interop. Changing
// the path or the scheme later would orphan every address already registered.

import { ed25519 } from '@noble/curves/ed25519';
import { slip10Ed25519 } from '../solana';
import { tonWalletRawAddress } from './wallet';
import { formatTonAddress } from './address';
import type { DerivedAddresses } from '../types';

const HARDENED = 0x80000000;

/**
 * SLIP-44 coin type 607 is TON's registered index.
 *
 * There is no de-facto standard path to match, because the wallets that matter
 * do not derive from a path at all (see the note above). So this is our own
 * convention and the only requirement on it is that it never changes.
 */
const PATH = (index: number) => [44 | HARDENED, 607 | HARDENED, index | HARDENED];

/**
 * The ed25519 keypair for an account index.
 *
 * TON signs with the 64-byte secret key, which in NaCl's layout is the 32-byte
 * seed followed by the 32-byte public key — not an expanded scalar. Built from
 * @noble/curves, already a dependency here, rather than from @ton/crypto:
 * that package exists in this file only to concatenate these two values, and
 * it was reaching the Mini App's startup bundle to do it. The test beside this
 * file pins both halves against @ton/crypto's own keyPairFromSeed.
 */
export function tonKeyPair(seed: Uint8Array, index = 0): {
  publicKey: Buffer;
  secretKey: Buffer;
} {
  const privateSeed = slip10Ed25519(seed, PATH(index)).key;
  const publicKey = Buffer.from(ed25519.getPublicKey(privateSeed));
  return { publicKey, secretKey: Buffer.concat([Buffer.from(privateSeed), publicKey]) };
}

/**
 * The wallet's raw address (`0:<hex>`) for an account index.
 *
 * Deliberately not a @ton/ton wallet object: that import belongs on the send
 * path, which loads only when sending. See ./cell.ts for the measurement that
 * made the split worth it.
 */
export function tonWalletAddress(seed: Uint8Array, index = 0): string {
  return tonWalletRawAddress(tonKeyPair(seed, index).publicKey);
}

/**
 * Derive the TON address and public key at the given account index.
 *
 * `eoa` is the user-friendly non-bounceable form (UQ…): the address a wallet
 * shows for receiving. Bounceable (EQ…) is the same account with a different
 * flag byte, and sending to a bounceable address that is not yet deployed
 * returns the funds to the sender — which is exactly what a fresh wallet of
 * ours is, so UQ is the correct one to publish.
 */
export function deriveTonAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const { publicKey } = tonKeyPair(seed, index);
  return {
    eoa: formatTonAddress(tonWalletRawAddress(publicKey)),
    publicKey: publicKey.toString('hex'),
  };
}

export * from './address';
export * from './wallet';
export * from './cell';
